import React, { useRef, useEffect } from 'react';
import Title from './Title';
import Header from './Header';
import Skills from './Skills';
import AboutCompany from './AboutCompany';
import { JobAd } from '__models__/job_ad';
import JobSection from './JobSection';
import Sidebar from './Sidebar';
import { GET_COMPANY_GQL } from './JobPageUtils';
import { apolloClient } from 'lib/graphql';
import { useQuery } from 'react-apollo';
import ApplyButton from './ApplyButton';
import WhatIsAContractor from './WhatIsAContractor';
import classNames from 'classnames';
import { isMobile } from 'react-device-detect';
import SubscribeButton from 'components/subscribeButton/SubscribeButton';
import LjSpinner from 'shared/ld_spinner/LjSpinner';

const styles = require('./JobPage.module.scss');

interface Props {
  bookmark: any;
  cancelReasons: any;
  rejectReasons: any;
  handshake: any;
  jobAd: JobAd;
  jobSuggestion: any;
  referralId: number;
  jobSearchFiltersAb?: string;
  quickApplyRestricted?: boolean;
}

export default function JobPage(props: Props) {
  const fixedHeader = !isMobile;
  const jobAdAttrs = props.jobAd.attributes;
  const jobPageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.getElementById('job-summary-content')?.remove();
  }, []);

  const JOBSECTIONS = [
    {
      title: 'Remote details',
      text: remoteDetailsText(),
      id: 'remoteDetails',
    },
    {
      title: 'Description',
      text: jobAdAttrs.role_description,
      id: 'description',
    },
    {
      title: 'Requirements',
      text: jobAdAttrs.main_requirements,
      id: 'requirements',
    },
    { title: 'Nice to have', text: jobAdAttrs.nice_to_have, id: 'niceToHave' },
    { title: 'Perks', text: jobAdAttrs.perks, id: 'perks' },
  ];

  function remoteDetailsText() {
    const timezoneMin = jobAdAttrs.min_timezone;
    const timezoneMax = jobAdAttrs.max_timezone;

    const timezoneString = () => {
      if (timezoneMin && timezoneMax) {
        if (timezoneMin === timezoneMax) {
          return `zone ${timezoneMin}`;
        } else {
          return `zones between ${timezoneMin} and ${timezoneMax}`;
        }
      }
    };

    if (jobAdAttrs.min_timezone || jobAdAttrs.max_timezone) {
      return `
          <p>
            To apply for this job you must be willing to work
            in the time ${timezoneString()}.
          </p>
          <p>
            ${jobAdAttrs.remote_details}
          </p>
        `;
    } else {
      return jobAdAttrs.remote_details;
    }
  }

  const { data, error, loading } = useQuery(GET_COMPANY_GQL, {
    client: apolloClient,
    variables: {
      id: jobAdAttrs.company_id.toString(),
      jobId: props.jobAd.id.toString(),
    },
  });

  // Merge real company (cover, slug, description) with masked name/logo from the
  // job's displayCompany. Talent-facing surfaces should always read from this.
  // When a brand is set, also blank the short pitch and flag the page so the
  // "Other jobs from..." section is suppressed (the related jobs are from the
  // real company, which would otherwise leak the identity).
  const brandName = data && data.job?.job?.displayCompany?.name;
  const isBranded = !!data?.job?.job?.displayCompany?.branded;
  const displayCompany = data && {
    ...data.company,
    name: brandName ?? data.company.name,
    logoUrl: data.job?.job?.displayCompany?.logoUrl ?? data.company.logoUrl,
    shortPitch: isBranded ? '' : data.company.shortPitch,
  };

  function renderTitle() {
    return (
      <>
        <Title
          applications={data.person.applications}
          cancelReasons={props.cancelReasons}
          companyId={jobAdAttrs.company_id}
          companySlug={data.company.slug}
          companyName={displayCompany.name}
          currentUser={data.currentUser}
          fixedHeader={fixedHeader}
          handshake={props.handshake}
          jobAd={props.jobAd}
          jobSuggestion={props.jobSuggestion}
          logo={displayCompany.logoUrl}
          referralId={props.referralId}
          shortPitch={displayCompany.shortPitch}
          title={jobAdAttrs.title}
          jobSearchFiltersAb={props.jobSearchFiltersAb}
          bookmark={props.bookmark}
          quickApplyRestricted={props.quickApplyRestricted}
        />
      </>
    );
  }

  return loading ? (
    <LjSpinner />
  ) : (
    <div ref={jobPageRef}>
      {fixedHeader && renderTitle()}
      <div className={styles.wrapper}>
        <div className={styles.leftSide}>
          {!fixedHeader && renderTitle()}
          <Header
            category={jobAdAttrs.category}
            citizenship={jobAdAttrs.citizenship}
            isContractor={jobAdAttrs.contractor}
            location={jobAdAttrs.location || jobAdAttrs.remote_working_label}
            officeLocations={jobAdAttrs.office_locations}
            experienceLabel={jobAdAttrs.experience_label}
            experienceMin={jobAdAttrs.experience_min}
            experienceMax={jobAdAttrs.experience_max}
            fixedHeader={fixedHeader}
            handshake={props.handshake}
            cancelReasons={props.cancelReasons}
            jobSearchFiltersAb={props.jobSearchFiltersAb}
            applications={data.person.applications}
            currentUser={data.currentUser}
            jobAd={props.jobAd}
            referralId={props.referralId}
            hiringBonus={jobAdAttrs.hiring_bonus}
            jobSuggestion={props.jobSuggestion}
            jobType={jobAdAttrs.job_type}
            niceToHaveLanguageLabel={jobAdAttrs.nice_to_have_language_label}
            preferredLanguageLabel={(jobAdAttrs.preferred_languages_labels || []).join(', ')}
            projectDuration={jobAdAttrs.project_duration}
            recruitmentManagedBy={isBranded ? data.company.name : undefined}
            relocationPaid={jobAdAttrs.relocation_paid}
            remoteWorkingLabel={jobAdAttrs.remote_working_label}
            rate={jobAdAttrs.rate}
            salary={jobAdAttrs.salary}
            visaSupport={jobAdAttrs.visa_support}
            locationIcon={jobAdAttrs.location_icon}
            isGlobalRemote={jobAdAttrs.is_global_remote}
            timezoneLabel={jobAdAttrs.timezone_label}
            bookmark={props.bookmark}
            containerRef={jobPageRef}
            companyName={displayCompany.name}
            companySlug={data.company.slug}
            quickApplyRestricted={props.quickApplyRestricted}
          />
          <Skills
            mustHaveSkills={jobAdAttrs.must_have_skills}
            otherRequiredSkills={jobAdAttrs.other_required_skills}
            niceToHaveSkills={jobAdAttrs.nice_to_have_skills}
          />

          {JOBSECTIONS.map(section => {
            return (
              section.text && (
                <section id={section.id} key={section.id}>
                  <JobSection title={section.title} text={section.text} />
                </section>
              )
            );
          })}
          {isMobile && (
            <>
              <div className={styles.applyButtonWrapper}>
                {!props.handshake && !props.jobSuggestion && (
                  <>
                    <ApplyButton
                      applications={data.person.applications}
                      currentUser={data.currentUser}
                      fixedHeader={fixedHeader}
                      handshake={props.handshake}
                      jobAd={props.jobAd}
                      jobSuggestion={props.jobSuggestion}
                      referralId={props.referralId}
                      style={styles.applyButton}
                      jobSearchFiltersAb={props.jobSearchFiltersAb}
                    />
                    <SubscribeButton
                      currentUser={data.currentUser}
                      job={{
                        id: props.jobAd.id,
                        title: props.jobAd.attributes.title,
                        stateName: props.jobAd.attributes.state_name,
                      }}
                      bookmark={props.bookmark}
                      size='large'
                    />
                  </>
                )}
              </div>

              <AboutCompany
                coverPhoto={data.company.coverPhotoUrl}
                companyId={jobAdAttrs.company_id}
                companyName={displayCompany.name}
                logo={displayCompany.logoUrl}
                shortPitch={displayCompany.shortPitch}
              />
            </>
          )}
          {jobAdAttrs.contractor && <WhatIsAContractor />}
        </div>
        {!isMobile && (
          <div
            className={classNames(styles.rightSide, {
              [styles.fixedTitle]: fixedHeader && !props.handshake,
              [styles.fixedTitleHandshake]: fixedHeader && props.handshake,
            })}
          >
            <Sidebar
              applications={data.person.applications}
              averageHiringProcess={data.company.averageHiringProcess}
              bookmark={props.bookmark}
              cancelReasons={props.cancelReasons}
              rejectReasons={props.rejectReasons}
              company={displayCompany}
              brandMasked={isBranded}
              companySubscriptions={data.person.companySubscriptions}
              currentUser={data.currentUser}
              fixedHeader={fixedHeader}
              handshake={props.handshake}
              jobAd={props.jobAd}
              jobSuggestion={props.jobSuggestion}
              jobs={data.jobs}
              referralId={props.referralId}
              jobSearchFiltersAb={props.jobSearchFiltersAb}
            />
          </div>
        )}
      </div>
    </div>
  );
}
